import { config } from "./config";

/**
 * Subscribes to a team's live channels over AppSync Events. Task 8.7, requirement 14.2.
 *
 * **Events are hints, not state** (design §5.3). The payload carries a type, a tournament and a
 * version, and nothing else — no finishing order, no participants — so it cannot be applied as a delta
 * even in principle. Every event means the same thing: re-read the truth. Three consequences worth
 * having, and they are the same ones the television relies on:
 *
 * - an unrecognised type from a future deploy behaves exactly like a recognised one, so an old browser
 *   tab cannot sit silently stale
 * - there is only one path by which state arrives, the ordinary T1 read
 * - a missed event is repaired by the next one, because the version alone reveals that the server moved
 *
 * Nothing here is load-bearing. If the socket never connects the app still works; the family just sees
 * changes when they next load rather than within the second.
 */

export interface RealtimeEvent {
  type: string;
  tournamentId?: string;
  version?: number;
}

export type RealtimeState = "connecting" | "live" | "offline";

interface Options {
  /** Cognito **access** token. The subscribe authorizer resolves it exactly as the HTTP API does. */
  token: string;
  teamId: string;
  onEvent: (event: RealtimeEvent) => void;
  onState: (state: RealtimeState) => void;
}

/**
 * The authorization subprotocol: a base64url-encoded JSON object prefixed with `header-`.
 *
 * `host` names the **HTTP** endpoint even though the socket opens against the realtime one, because
 * AppSync validates the connection against the HTTP host.
 */
function authProtocol(token: string): string {
  const header = JSON.stringify({ host: config.events.httpDomain, Authorization: token });
  const encoded = btoa(header).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `header-${encoded}`;
}

export function connectRealtime({ token, teamId, onEvent, onState }: Options): () => void {
  let socket: WebSocket | null = null;
  let retry: number | undefined;
  let attempt = 0;
  let closed = false;

  // Everything in one team's world. `/tournament/{teamId}/*` is what makes a new tournament visible at
  // all — an event about a tournament that did not exist a moment ago has no channel of its own to
  // arrive on.
  const channels = [`/tournament/${teamId}/*`, `/garage/${teamId}`, `/roster/${teamId}`];
  const authorization = { host: config.events.httpDomain, Authorization: token };

  const open = () => {
    if (closed) return;
    onState(attempt === 0 ? "connecting" : "offline");

    socket = new WebSocket(`wss://${config.events.realtimeDomain}/event/realtime`, [
      authProtocol(token),
      "aws-appsync-event-ws",
    ]);

    socket.onopen = () => socket?.send(JSON.stringify({ type: "connection_init" }));

    socket.onmessage = (message) => {
      const frame = JSON.parse(message.data as string) as Record<string, unknown>;
      switch (frame.type) {
        case "connection_ack":
          for (const channel of channels) {
            socket?.send(
              JSON.stringify({ type: "subscribe", id: crypto.randomUUID(), channel, authorization }),
            );
          }
          break;
        case "subscribe_success":
          attempt = 0;
          onState("live");
          break;
        case "data": {
          const raw = frame.event;
          if (typeof raw !== "string") return;
          try {
            onEvent(JSON.parse(raw) as RealtimeEvent);
          } catch {
            // Unparseable is still a signal that something changed, and §5.3 says to go and look.
            onEvent({ type: "unknown" });
          }
          break;
        }
        // "ka" keep-alives need no handling: a dead socket surfaces as onclose.
        default:
          break;
      }
    };

    const reopen = () => {
      if (closed) return;
      onState("offline");
      // Jittered exponential backoff, as AWS asks for. Jitter matters even here: a television and two
      // phones reconnecting in lockstep after the router restarts is a small thundering herd.
      const ceiling = Math.min(2 ** Math.min(attempt, 6), 60);
      attempt += 1;
      retry = window.setTimeout(open, 1000 * (ceiling / 2 + Math.random() * (ceiling / 2)));
    };

    socket.onclose = reopen;
    socket.onerror = () => socket?.close();
  };

  open();

  return () => {
    closed = true;
    if (retry) window.clearTimeout(retry);
    socket?.close();
  };
}
